<template>
  <all-analytics-capture
    v-if="portalUpdatesJan2026Enabled && showCapture"
    ref="captureRef"
    :homepage-sections="homepageSections"
    :page-extras="pageExtras"
    :homepage-busy="homepageBusy"
  />
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, ref } from 'vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useSubsectionDownload from '@/composables/useSubsectionDownload';
import AllAnalyticsCapture from '@/pages/analyticsHomepage/components/AllAnalyticsCapture.vue';

// The homepage passes getters for its already-rendered sections and Summary
// snapshot KPIs so the capture host can reuse them instead of re-mounting (and
// re-fetching) copies of the homepage-native components. `homepageBusy` lets the
// capture wait out an in-flight refetch of those live sections before capturing.
defineProps({
  homepageSections: { type: Function, default: null },
  pageExtras: { type: Function, default: null },
  homepageBusy: { type: Function, default: null },
});

const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { isGenerating, downloadSheets } = useSubsectionDownload();
const instance = getCurrentInstance();

const preparing = ref(false);
const showCapture = ref(false);
const captureRef = ref(null);

const busy = computed(() => preparing.value || isGenerating.value);

function toast(message) {
  instance?.proxy?.$appToast?.(message, { title: 'Download full report', variant: 'danger' });
}

async function download() {
  if (busy.value) {
    return;
  }
  preparing.value = true;
  showCapture.value = true;
  try {
    await nextTick();
    await captureRef.value?.ensureReady();
    const sections = captureRef.value?.collectSections() || [];
    if (!sections.length) {
      toast('No analytics data is available to download yet. Try again once the tabs have loaded.');
      return;
    }
    const pageExtras = captureRef.value?.getPageExtras() || null;
    // Keep the capture host mounted through downloadSheets — it reads the live
    // canvases while building the PDF — then tear it down in the finally block.
    const result = await downloadSheets(sections, { format: 'pdf', pageExtras });
    if (!result?.success) {
      toast('Something went wrong generating the download. Please try again.');
    }
  } finally {
    showCapture.value = false;
    preparing.value = false;
  }
}

defineExpose({ download, busy });
</script>
